/**
 * @file organization.js
 * @description Servicio de la Organization del user autenticado actual
 *              (no del platform admin — para eso ver `services/tenant.js`).
 *              La consume cualquier user logueado: dashboard lee al login
 *              para hidratar branding + features + flags en runtime.
 */
import api from "./api";

const organizationService = {};

/**
 * @description UC-REG-067 + UC-REG-068. Devuelve los datos públicos de la
 *              org del user autenticado. Lo llama el dashboard al login para:
 *              - aplicar branding (logo, color primario) en runtime
 *              - leer features encendidas
 *              - leer sharePatientsAcrossDoctors para tooltips/UX
 *
 *              Shape devuelto:
 *              {
 *                _id, key, name, timezone, kind, domain,
 *                branding: { logoUrl, faviconUrl, colorPrimary, colorAccent,
 *                             fontFamily, footerCopy },
 *                features: { [key]: boolean },
 *                notifications: { hasCustomEmailFrom, patientAssignmentEnabled },
 *                sharePatientsAcrossDoctors: boolean
 *              }
 * @returns {Promise<Object>}
 */
organizationService.getMe = function () {
  return api.get("/organization/me").then((res) => res.data);
};

/**
 * @description UC-REG-088 — Obtiene las reglas de mora configuradas para
 *              la org actual. Si nunca se setearon, devuelve los defaults
 *              (1-30 / 31-60 / 61+ con autoSuspend: true).
 * @returns {Promise<{earlyDefault, lateDefault, suspended, autoSuspend}>}
 */
organizationService.getMemberPaymentRules = function () {
  return api
    .get("/organization/me/member-payment-rules")
    .then((res) => res.data);
};

/**
 * @description UC-REG-088 — Actualiza las reglas de mora de la org.
 *              Backend valida que sean crecientes (early < late < suspended)
 *              y devuelve 400 si no.
 * @param {Object} rules
 * @returns {Promise<Object>} las reglas guardadas
 */
organizationService.updateMemberPaymentRules = function (rules) {
  return api
    .put("/organization/me/member-payment-rules", rules)
    .then((res) => res.data);
};

export default organizationService;
